/**
 * Notification Center contract, matching the shipped read API
 * (`crm-core` `controller/v1/core/notification`).
 */

export const NOTIFICATION_CATEGORY = {
  JOB: 'JOB',
  CUSTOMER_EVENT: 'CUSTOMER_EVENT',
} as const

export type NotificationCategory = typeof NOTIFICATION_CATEGORY[keyof typeof NOTIFICATION_CATEGORY]

export const NOTIFICATION_STATUS = {
  QUEUED: 'QUEUED',
  RUNNING: 'RUNNING',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
  PARTIAL: 'PARTIAL',
} as const

export type NotificationStatus = typeof NOTIFICATION_STATUS[keyof typeof NOTIFICATION_STATUS]

/** One producer. The server's pref-key catalog gates these, so the set grows without a release. */
export const NOTIFICATION_SOURCE = {
  SNAP_TO_ROAD: 'SNAP_TO_ROAD',
  INVOICE_BATCH: 'INVOICE_BATCH',
  INVOICE_DELIVERY: 'INVOICE_DELIVERY',
  EXPORT: 'EXPORT',
  ACCOUNT_DEACTIVATION: 'ACCOUNT_DEACTIVATION',
  ACCOUNT_REACTIVATION: 'ACCOUNT_REACTIVATION',
  RENTAL_FEES: 'RENTAL_FEES',
  SUSPENSION: 'SUSPENSION',
  AUTOPAY: 'AUTOPAY',
  PAYMENT_METHOD: 'PAYMENT_METHOD',
  ROUTE_OPTIMIZATION: 'ROUTE_OPTIMIZATION',
} as const

export type NotificationSource = typeof NOTIFICATION_SOURCE[keyof typeof NOTIFICATION_SOURCE]

/** Shared by a notification's own entity and by each drill-down item's ref. */
export const NOTIFICATION_ENTITY_TYPE = {
  ACCOUNT: 'ACCOUNT',
  WORK_ORDER: 'WORK_ORDER',
  INVOICE: 'INVOICE',
  ROUTE: 'ROUTE',
  LOCATION: 'LOCATION',
} as const

export type NotificationEntityType = typeof NOTIFICATION_ENTITY_TYPE[keyof typeof NOTIFICATION_ENTITY_TYPE]

export const NOTIFICATION_ITEM_OUTCOME = {
  FAILED: 'FAILED',
  SKIPPED: 'SKIPPED',
} as const

export type NotificationItemOutcome = typeof NOTIFICATION_ITEM_OUTCOME[keyof typeof NOTIFICATION_ITEM_OUTCOME]

export const NOTIFICATION_SEVERITY = {
  ERROR: 'error',
  WARNING: 'warning',
  SUCCESS: 'success',
  INFO: 'info',
} as const

/** Urgency, independent of category. Maps 1:1 to a theme palette family. */
export type NotificationSeverity = typeof NOTIFICATION_SEVERITY[keyof typeof NOTIFICATION_SEVERITY]

/**
 * Extras the contract carries in the free-form `payload` rather than as columns.
 * Each entry is a gap raised with the BE owner; when a column lands, it moves up
 * to `Notification` and drops out of here.
 *
 * `severity` is only needed for customer events — a job's urgency is already
 * derivable from `status` (see `severityOf`).
 */
export type NotificationPayload = {
  details?: string
  severity?: NotificationSeverity
  /** Route optimization writes its job here: enough to reopen and describe the run without a fetch. */
  jobId?: string
  routeId?: string
  serviceDate?: string
  scope?: 'MASTER' | 'DAILY'
  /** Absent while the dispatcher has yet to accept or reject the run. */
  resolution?: 'APPLIED' | 'REJECTED'
}

export type Notification = {
  id: string
  category: NotificationCategory
  source: NotificationSource
  status: NotificationStatus
  title: string
  body?: string
  entityType?: NotificationEntityType
  entityId?: string
  /** Frozen at write by the producer, so a later rename cannot rewrite history. */
  entityLabel?: string
  totalCount?: number
  succeededCount?: number
  failedCount?: number
  /** A notification may carry no items at all, so the drill-down keys off this rather than the row. */
  hasItems: boolean
  payload?: NotificationPayload
  readAt?: string
  /** The producer's clock — display this. */
  occurredAt: string
  /** Sort key: read and update both bump it, so it beats a skewed producer clock. */
  lastActivityAt: string
}

/** Only `outcome` is guaranteed — every ref and reason column is nullable. */
export type NotificationItem = {
  refType?: NotificationEntityType
  refId?: string
  refLabel?: string
  outcome: NotificationItemOutcome
  reasonCode?: string
  reasonMessage?: string
}

/** Absence of a row means enabled, so the catalog comes from the server, never hardcoded. */
export type NotificationPreference = {
  prefKey: string
  label: string
  description: string
  category: NotificationCategory
  enabled: boolean
}

export type NotificationListResponse = {
  notifications: Notification[]
  totalItems: number
  totalPages: number
}

export type NotificationItemListResponse = {
  items: NotificationItem[]
  totalItems: number
  totalPages: number
  truncated: boolean
}

/** Repeated singular params — `?source=EXPORT&source=INVOICE_BATCH`. */
export type NotificationListParams = {
  category?: NotificationCategory[]
  status?: NotificationStatus[]
  source?: NotificationSource[]
  /** Omitted means both; false is unread. */
  read?: boolean
  search?: string
  /** ISO dates; `occurredTo` is inclusive of that day. */
  occurredFrom?: string
  occurredTo?: string
  page?: number
  size?: number
}

export const isUnread = (notification: Notification): boolean => !notification.readAt
